import React, { useState, useCallback, useEffect } from 'react';
import { Clock, Users2, ChevronRight, Building2, Mail, Phone, UserPlus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import SummaryModal from '../components/SummaryModal';
import ContactDetailsModal from '../components/ContactDetailsModal';
import AddContactModal from '../components/AddContactModal';
import { supabase } from '../utils/supabase';

interface Summary {
  id: string;
  title: string;
  date: string;
  time: string;
  summary: string | null;
  double: {
    name: string;
  };
  follow_ups: Array<{
    id: string;
    title: string;
    description: string | null;
    status: 'pending' | 'in_progress' | 'completed';
    assignee: string | null;
  }>;
}

interface Summary {
  id: string;
  title: string;
  date: string;
  time: string;
  summary: string | null;
  double: {
    name: string;
  };
  follow_ups: Array<{
    id: string;
    title: string;
    description: string | null;
    status: 'pending' | 'in_progress' | 'completed';
    assignee: string | null;
  }>;
}

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  company: string | null;
  role: string | null;
  email: string | null;
  phone: string | null;
  created_at: string;
}

const CRM = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [contactSummaries, setContactSummaries] = useState<Summary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<number | null>(null);
  const [selectedSummary, setSelectedSummary] = useState<Summary | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const { data, error } = await supabase
          .from('contacts')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setContacts(data || []);
      } catch (err) {
        console.error('Error fetching contacts:', err);
        setError('Failed to load contacts');
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  useEffect(() => {
    if (selectedContact) {
      const fetchSummaries = async () => {
        try {
          const { data, error } = await supabase
            .from('summaries')
            .select(`
              *,
              double:double_id (name),
              follow_ups (*)
            `)
            .eq('contact_id', selectedContact)
            .order('date', { ascending: false });

          if (error) throw error;
          setContactSummaries(data || []);
        } catch (err) {
          console.error('Error fetching summaries:', err);
          setError('Failed to load summaries');
        }
      };

      fetchSummaries();
    } else {
      setContactSummaries([]);
    }
  }, [selectedContact]);

  const handleAddContact = useCallback((contact: { firstName: string; lastName: string; email: string; phone: string }) => {
    const newContact = {
      id: contacts.length + 1,
      name: `${contact.firstName} ${contact.lastName}`,
      company: '',
      role: '',
      email: contact.email,
      phone: contact.phone,
      addedAt: new Date().toISOString()
    };
    
    setContacts(prev => [...prev, newContact]);
    setShowAddModal(false);
  }, [contacts]);

  return (
    <div className="animate-fade-in">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">CRM</h1>
          <p className="text-gray-600">Manage your contacts and view conversation history.</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="button-primary flex items-center gap-2"
        >
          <UserPlus className="h-5 w-5" />
          Add Contact
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Contacts List */}
        <div className="lg:col-span-1 space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
              <p className="mt-2 text-gray-600">Loading contacts...</p>
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <p className="text-gray-600">No contacts found. Add your first contact to get started.</p>
            </div>
          ) : contacts.map((contact) => (
            <button
              key={contact.id}
              onClick={() => {
                setSelectedContact(contact.id);
                setShowDetailsModal(true);
              }}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedContact === contact.id
                  ? 'bg-primary/5 border-primary shadow-none -translate-y-1'
                  : 'bg-white border-dark shadow-retro hover:shadow-none hover:-translate-y-1'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-semibold">
                    {contact.first_name[0]}{contact.last_name[0]}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{contact.first_name} {contact.last_name}</h3>
                  <div className="text-sm text-gray-600 space-y-1 mt-1">
                    <div className="flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      <span>{contact.role} at {contact.company}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      <span>{contact.email}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Phone className="h-4 w-4" />
                      <span>{contact.phone}</span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Conversation History */}
        <div className="lg:col-span-2">
          {selectedContact ? (
            <>
              <h2 className="text-xl font-semibold mb-4">Conversation History</h2>
              <div className="space-y-4">
                {contactSummaries.length > 0 ? (
                  contactSummaries.map((summary) => (
                    <button
                      key={summary.id}
                      onClick={() => setSelectedSummary(summary)}
                      className="w-full text-left bg-white p-6 rounded-lg border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{summary.title}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              <span>{summary.date} at {summary.time} • {summary.double}</span>
                            </div>
                          </div>
                          <p className="mt-3 text-gray-600 line-clamp-2">{summary.summary}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <p className="text-gray-600">No conversations found with this contact.</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <p className="text-gray-600">Select a contact to view their conversation history.</p>
            </div>
          )}
        </div>
      </div>

      <SummaryModal
        summary={selectedSummary}
        onClose={() => setSelectedSummary(null)}
      />
      
      {showAddModal && (
        <AddContactModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddContact}
        />
      )}
      
      {showDetailsModal && selectedContact && (
        <ContactDetailsModal
          contact={contacts.find(c => c.id === selectedContact)!}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedContact(null);
          }}
        />
      )}
    </div>
  );
};

export default CRM;